import * as R from 'ramda'

/**
 *
 * @param {Record<Number, DynamicQueueTime} queueTimes A map of POI Ids to dynamic queue times
 * @param {Record<Number, String>} labels A map of POI Ids to map labels
 * @param {*} feature A GeoJson feature
 * @returns feature
 */
export const mutateSecurityCheckpointLabel = (T, queueTimes, labels) => feature => {
  const id = feature.properties.id
  const dynamicData = queueTimes[id]
  const label = labels[id]
  if (dynamicData) { // should we only show this message when "timeIsReal" is true?
    const { queueTime, isTemporarilyClosed } = dynamicData
    const secondaryText = isTemporarilyClosed ? T('ui:Closed') : T('ui:_xx_ minute wait', { count: queueTime })
    feature.properties.text = `${label}\n${secondaryText}`
  }
  return feature
}

export const processParkingPOIS = (poiMap) => {
  const idValuesMap =
    R.pipe(
      R.filter(poi => poi.category === 'parking'),
      R.map(poi => {
        const da = poi.dynamicAttributes
        if (!da)
          throw Error(`No dynamicAttributes defined for parking POI ${poi.poiId}`)
        const age = (Date.now() - poi.timestamp) / 1000 // how long ago this was updated by backend (in seconds)
        const props = (age < da['parking.timeToLive']) // if this update is recent enough, consider it "valid"
          ? R.pick(['lotStatus', 'rateDay', 'rateHour', 'timeIsReal', 'timeToTerminal1', 'timeToTerminal2'], poi)
          : { lotStatus: da['parking.default'], rateDay: '$ -', rateHour: '$ -', timeIsReal: false }

        return { ...props, lastUpdated: poi.timestamp, lotName: poi.lotName }
      }))(poiMap)
  return idValuesMap
}

export const processOpenClosedPois = (poiMap) => {
  const pathToOpenClosedData = ['dynamicData', 'openClosed']
  const openClosedPois = R.filter(R.hasPath(pathToOpenClosedData), poiMap)
  const openClosedStatuses = R.map(R.path(pathToOpenClosedData), openClosedPois)

  if (R.all(R.both(R.has('isOpen'), R.has('expiration')), R.values(openClosedStatuses))) {
    const isExpired = R.pipe(R.prop('expiration'), R.lt(Date.now()))
    const idValuesMap = R.filter(isExpired, openClosedStatuses)

    return idValuesMap
  } else {
    throw Error('Open Closed poi status is malformed.')
  }
}

/*
    API response: https://gitlab.com/locuslabs/core-data-team/json-schemas/-/blob/develop/src/api-marketplace/dynamic-queue-data.json
   */
export const processSecurityWaitTimes = (poiMap) => {
  const queuePath = ['dynamicData', 'queue']
  const queuePois = R.filter(R.hasPath(queuePath), poiMap)
  const queues = R.map(R.path(queuePath), queuePois)
  return R.map(queue => ({
    isTemporarilyClosed: queue.isTemporarilyClosed,
    queueTime: queue.queueTime,
    timeIsReal: !queue.isQueueTimeDefault && queue.expiration > Date.now(),
    lastUpdated: Date.now()
  }), queues)
}

export const processRoutingPois = (poiMap) => {
  const now = Date.now()

  return R.filter(
    poi => {
      const navGraphHidden = R.path(['dynamicData', 'dynamic-routing', 'navGraphHidden'], poi)
      const expiration = R.path(['dynamicData', 'dynamic-routing', 'expiration'], poi)
      return navGraphHidden === true && expiration > now
    },
    poiMap
  )
}
